import { Component, Prop, h, State, Watch } from '@stencil/core';
import { IconName } from './icons';
import FeatherSprite from './icons/feather-sprite.svg';

import { Size, SizeType } from '../../types';

const sizeValues: Record<SizeType, number> = {
  [Size.xlarge]: 96,
  [Size.large]: 64,
  [Size.medium]: 32,
  [Size.small]: 24,
  [Size.xsmall]: 18,
} as const;
export type IconSize = keyof typeof Size;
@Component({
  tag: 'anchor-icon',
  styleUrl: 'icon.scss',
  shadow: true,
})
export class Icon {
  /**
   * Set which icon is displayed with this prop.
   */
  @Prop() name: IconName = 'activity';

  /**
   * We recommend using one of the strings. Number option included for the sake of flexibility.
   */
  @Prop() size: SizeType | number = 'medium';

  /**
   * ⚠️ This prop is deprecated. Please use the CSS variable --icon-color to set your own color for the icon.
   */
  @Prop() color: string = 'currentColor';

  @Prop() stroke: number;

  // ideally we would use https://github.com/feathericons/feather#2-use
  // Unfortunately getAssetPath in stenciljs is not optimal to use atm. Regex to the rescue!
  // eslint-disable-next-line class-methods-use-this
  private getIconDef = (name: IconName, svgString: string): string => {
    const tag = 'symbol';
    const regex = new RegExp(`<${tag}[^>]*id[\\s]?=[\\s]?['"]${name}['"][\\s\\S]*?</${tag}>`, 'm');
    const result = regex.exec(svgString);
    if (!!result && Array.from(result).length === 1) {
      // all good
      return result[0].replace(new RegExp(`${tag}`, 'mg'), 'svg');
    }
    const warning = `Unknown icon: ${name}`;
    console.warn(warning);
    return `${name}?!`;
  };

  @State() svg: string;

  @Watch('name')
  handleNameChange(newValue: string) {
    this.svg = this.getIconDef(newValue as IconName, FeatherSprite);
  }

  componentWillLoad() {
    this.svg = this.getIconDef(this.name, FeatherSprite);
  }

  render() {
    const size = sizeValues[this.size] || this.size;
    // handle custom rules. https://tryg01.atlassian.net/wiki/spaces/DesignOps/pages/3614146571/Icon+Library+setup
    let strokeWidth = this.stroke ?? 1.5;
    let fill = false;
    if (this.name.indexOf('chevron-') !== -1) {
      const specialRule = size >= sizeValues.small && size < sizeValues.large;
      strokeWidth += specialRule && !this.stroke ? 1 : 0;
    }
    if (this.name.indexOf('star-filled') !== -1) {
      fill = true;
    }
    return (
      <div
        class={`anchor-c-icon ${fill ? 'anchor-c-icon--fill' : ''}`}
        innerHTML={this.svg}
        aria-hidden="true"
        style={{
          'color': this.color,
          'fill': this.color,
          '--size': `${size / 16}rem`,
          '--stroke': `${strokeWidth / 16}rem`,
        }}
      />
    );
  }
}
